import { ChangeEvent, DragEvent, useEffect, useRef, useState } from "react";
import { OrderBoxHeader } from "./header/OrderBoxHeader";
import styled, { useTheme } from "styled-components";

import { OrderBoxResponsesList } from "./responses/OrderBoxResponsesList";
import { useIsMedia } from "src/apps/hooks/useIsMedia";
import { OrderModel } from "src/contexts/documentationArea/domain/OrderModel";
import { OrderOtherDocument } from "src/contexts/documentationArea/domain/OrderOtherDocument";
import { OrderPayslip } from "src/contexts/documentationArea/domain/OrderPayslip";
import { useUploader } from "src/apps/hooks/uploadFiles/useUploader";
import { Icon, ICON_SIZES } from "src/apps/components/icon/Icon";
import { ICON_PATHS } from "src/apps/components/icon/IconSVGPaths";
import { T } from "src/apps/components/i18n/Translation";
import { TextContent } from "src/apps/components/typography/Texts";
import { ProcessingOrderBoxUploader } from "./uploader/processing/ProcessingOrderBoxUploader";
import { Button } from "src/apps/components/button/Button";
import { LuArrowUpFromLine } from "react-icons/lu";
import { useTranslations } from "src/apps/hooks/useTranslations";
import { colors } from "@permisso/permisso-components";
import { TEST_IDS } from "src/apps/config/testIds";
import { ACCEPTED_UPLOAD_FILE_TYPES } from "src/apps/providers/uploaders/constants";

interface OrderBoxProps {
  order: OrderModel | OrderPayslip | OrderOtherDocument;
  dataTestId?: string;
}

export const OrderBox = ({ order, dataTestId }: OrderBoxProps) => {
  const { blackColor } = useTheme();
  const { t } = useTranslations();
  const [isExpanded, setIsExpanded] = useState(true);
  const [isDragOver, setIsDragOver] = useState(false);
  const dragCounterRef = useRef(0);
  const hiddenInputRef = useRef<HTMLInputElement>(null);
  const { isMobile } = useIsMedia();
  const { convertAndUploadActorOrderFiles } = useUploader();

  const toggleExpanded = () => {
    setIsExpanded((prev) => !prev);
  };

  const hasValidResponses = order.hasValidResponses();
  const hasPendingReviewResponses = order.hasPendingReviewResponses();

  useEffect(() => {
    if ((hasValidResponses || hasPendingReviewResponses) && isExpanded && isMobile) {
      setIsExpanded(false);
    }
  }, [isMobile, hasValidResponses, hasPendingReviewResponses]);

  const convertAndUpload = (fileList: FileList | null) => {
    const files = Array.from(fileList ?? []);
    if (files.length === 0) return;
    convertAndUploadActorOrderFiles(order.getId(), files);
  };

  const onHiddenInputChange = async (event: ChangeEvent<HTMLInputElement>) => {
    convertAndUpload(event.target.files);
    event.target.value = "";
  };

  const onUploadClick = () => {
    if (order.isProcessing()) {
      return;
    }

    hiddenInputRef.current?.click();
  };

  const supportsFileDrag = (event: DragEvent<HTMLDivElement>) => {
    return Array.from(event.dataTransfer.types).includes("Files");
  };

  const onDragEnter = (event: DragEvent<HTMLDivElement>) => {
    if (order.isProcessing() || !order.canManuallyUpload() || isMobile || !supportsFileDrag(event))
      return;
    event.preventDefault();
    dragCounterRef.current += 1;
    setIsDragOver(true);
  };

  const onDragOver = (event: DragEvent<HTMLDivElement>) => {
    if (order.isProcessing() || !order.canManuallyUpload() || isMobile || !supportsFileDrag(event))
      return;
    event.preventDefault();
    if (!isDragOver) setIsDragOver(true);
  };

  const onDragLeave = (event: DragEvent<HTMLDivElement>) => {
    if (order.isProcessing() || !order.canManuallyUpload() || isMobile) return;
    event.preventDefault();
    dragCounterRef.current = Math.max(dragCounterRef.current - 1, 0);
    if (dragCounterRef.current === 0) {
      setIsDragOver(false);
    }
  };

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    if (order.isProcessing() || !order.canManuallyUpload() || isMobile || !supportsFileDrag(event))
      return;

    dragCounterRef.current = 0;
    setIsDragOver(false);

    if (event.defaultPrevented) return;

    event.preventDefault();
    convertAndUpload(event.dataTransfer.files);
  };

  const orderBoxTestId =
    dataTestId ??
    (order.isModel()
      ? TEST_IDS.orders.box(order.getRequest().type)
      : TEST_IDS.orders.box(order.getType()));

  return (
    <OrderBoxContainer
      data-testid={orderBoxTestId}
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {isDragOver && order.canManuallyUpload() && !order.isProcessing() ? (
        <DragOverlay>
          <DragOverlayContent $borderColor={blackColor}>
            <Icon size={ICON_SIZES.s14} color={blackColor} path={ICON_PATHS.PLUS_LARGE} />
            <TextContent $color={blackColor}>
              <T k="documentation_area.uploader_box.upload_your_documents" />
            </TextContent>
          </DragOverlayContent>
        </DragOverlay>
      ) : null}

      <>
        <OrderBoxHeader
          order={order}
          onToggleClick={toggleExpanded}
          onUploadClick={onUploadClick}
          isExpanded={isExpanded}
        />
        <HiddenUploadInput
          ref={hiddenInputRef}
          onChange={onHiddenInputChange}
          accept={ACCEPTED_UPLOAD_FILE_TYPES}
          multiple
          data-testid={TEST_IDS.orders.fileInput}
        />
        {order.isProcessing() ? (
          <ProcessingOrderBoxUploader />
        ) : (
          <>{order.hasResponses() && isExpanded ? <OrderBoxResponsesList order={order} /> : null}</>
        )}
        {order.canManuallyUpload() && isMobile ? (
          <Button
            dataTestId={TEST_IDS.orders.uploadButton}
            variant="outline"
            text={t("common.upload_file")}
            onClick={onUploadClick}
            disabled={order.isProcessing()}
            leftIcon={<LuArrowUpFromLine size={20} />}
          />
        ) : null}
      </>
    </OrderBoxContainer>
  );
};

const OrderBoxContainer = styled.div`
  position: relative;
  gap: 0.75rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 3.875rem;
  border: 0.0625rem solid ${colors.grey400};
  border-radius: 0.5rem;
  padding: 1rem;
`;

const HiddenUploadInput = styled.input.attrs({ type: "file" })`
  display: none;
`;

const DragOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.7);
  border-radius: 0.5rem;
  pointer-events: none;
  z-index: 10;
  display: flex;
  align-items: center;
`;

const DragOverlayContent = styled.div<{ $borderColor: string }>`
  width: 100%;
  height: 100%;
  border-radius: 0.375rem;
  border: 1px dashed ${({ $borderColor }) => $borderColor};
  background-color: ${colors.grey300};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
`;
